import { Box, Grid, Heading, Separator, Text } from '@radix-ui/themes';
import { CSVBall } from 'classes/csv-ball';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { BuildPriorityCallout } from 'components/common/build-priority-callout';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFileUploader } from 'components/common/file-uploader';
import { CommonSwitchInput } from 'components/common/form/switch';
import { CommonLink } from 'components/common/link';
import { CommonRadio } from 'components/common/radio';
import env from 'config';
import { MachineContext } from 'contexts/machine.context';
import { CookieKey } from 'enums/cookies.enums';
import { PitchUploadStep, PitchUploaderTab } from 'enums/pitch-uploader';
import { t } from 'i18next';
import { ALLOWED_MACHINE_IDS_FOR_ZERO_BREAKING_SEAMS } from 'interfaces/cookies/i-app.cookie';
import { TextMime } from 'lib_ts/enums/files.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICSVRow } from 'lib_ts/interfaces/csv';
import { first } from 'lodash';
import { useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { MainService } from 'services/main.service';
import { useCookiesStore } from 'stores/use-cookies-store';
import { usePitchUploaderStore } from 'stores/use-pitch-uploader-store';
import { useVideosStore } from 'stores/use-videos-store';

// do not try to process files with more lines of data than this value
const MAX_LINES = 500;
const TOTAL_COLS = '4';
const ALL_COLS = 'span 4';
const LEFT_COLS = 'span 3';

export const PitchUploaderFileControls = () => {
  const options = useCookiesStore((s) => s.app.pitch_upload_options);

  const { machine } = useContext(MachineContext);

  const videos = useVideosStore((s) => s.videos);

  // allow lookup of a video's _id by VideoTitle or _id
  const lookupDict = useMemo(() => {
    const output: { [key: string]: string } = {};

    videos.forEach((v) => {
      const attrKey = `${v.VideoTitle}`.trim();

      if (attrKey && !output[attrKey]) {
        /** only insert for first encounter */
        output[attrKey] = v._id;
      }

      // also add the video _id to the dictionary
      output[v._id] = v._id;
    });

    return output;
  }, [videos]);

  const avgPitchTooltip = useMemo(() => {
    switch (options.average_pitch) {
      case 'append': {
        return ['pu.append-msg-1', 'pu.append-msg-2', 'pu.append-msg-3']
          .map((l) => `- ${t(l)}`)
          .join('\n');
      }

      case 'replace': {
        return ['pu.replace-msg-1', 'pu.replace-msg-2', 'pu.replace-msg-3']
          .map((l) => `- ${t(l)}`)
          .join('\n');
      }

      default: {
        return undefined;
      }
    }
  }, [options.average_pitch]);

  return (
    <ErrorBoundary identifier="PitchUploaderFileControls">
      <Grid columns={TOTAL_COLS} gap={RADIX.FLEX.GAP.MD}>
        <Box gridColumn={ALL_COLS}>
          <Heading size={RADIX.HEADING.SIZE.SM}>
            {t('pu.input-priority')}
          </Heading>
        </Box>

        <Box gridColumn={LEFT_COLS}>
          <Text color={RADIX.COLOR.SECONDARY}>
            {t('pu.input-priority-msg')}
          </Text>

          {options.priority === BuildPriority.Breaks && (
            <ReactMarkdown children={t('pu.break-priority-msg')} />
          )}
        </Box>
        <Box>
          <CommonRadio
            id="pitch-uploader-build-priority"
            name="priority"
            options={[
              {
                label: 'pd.spins',
                value: BuildPriority.Spins,
              },
              {
                label: 'pd.breaks',
                value: BuildPriority.Breaks,
              },
            ]}
            value={
              options.priority === BuildPriority.Default
                ? // coalesce default > spins
                  BuildPriority.Spins
                : options.priority
            }
            onChange={(e) => {
              useCookiesStore.getState().setCookie(CookieKey.app, {
                pitch_upload_options: {
                  ...options,
                  priority: e.target.value,
                },
              });
            }}
          />
        </Box>

        <Box gridColumn={ALL_COLS}>
          <BuildPriorityCallout priority={options.priority} />
        </Box>

        {env.enable.pitch_upload_avg_release && (
          <>
            <Box gridColumn={ALL_COLS}>
              <Separator size="4" />
            </Box>

            <Box gridColumn={ALL_COLS}>
              <Heading size={RADIX.HEADING.SIZE.SM}>
                {t('pu.average-release-slot')}
              </Heading>
            </Box>

            <Box gridColumn={LEFT_COLS}>
              <Text color={RADIX.COLOR.SECONDARY}>
                {t('pu.average-release-slot-msg')}
              </Text>
            </Box>
            <Box>
              <CommonSwitchInput
                id="pitch-uploader-avg-release"
                checked={options.average_release}
                onCheckedChange={(v) => {
                  useCookiesStore.getState().setCookie(CookieKey.app, {
                    pitch_upload_options: {
                      ...options,
                      average_release: v,
                    },
                  });
                }}
              />
            </Box>
          </>
        )}

        {env.enable.pitch_upload_avg_chars && (
          <>
            <Box gridColumn={ALL_COLS}>
              <Separator size="4" />
            </Box>

            <Box gridColumn={ALL_COLS}>
              <Heading size={RADIX.HEADING.SIZE.SM}>
                {t('pu.average-pitch-characteristics')}
              </Heading>
            </Box>

            <Box gridColumn={LEFT_COLS}>
              <Text color={RADIX.COLOR.SECONDARY}>
                <ReactMarkdown
                  children={t('pu.average-pitch-characteristics-msg')}
                />
              </Text>
            </Box>
            <Box>
              <CommonRadio
                id="pitch-uploader-avg-pitch"
                name="average_pitch"
                title={avgPitchTooltip}
                options={[
                  { label: t('pd.append'), value: 'append' },
                  { label: t('pd.replace'), value: 'replace' },
                ]}
                value={options.average_pitch}
                onChange={(e) => {
                  useCookiesStore.getState().setCookie(CookieKey.app, {
                    pitch_upload_options: {
                      ...options,
                      average_pitch: e.target.value,
                    },
                  });
                }}
                placeholder="Skip"
                optional
              />
            </Box>
          </>
        )}

        {(!env.identifier.startsWith('prod') ||
          ALLOWED_MACHINE_IDS_FOR_ZERO_BREAKING_SEAMS.includes(
            machine.machineID
          )) && (
          <>
            <Box gridColumn={ALL_COLS}>
              <Separator size="4" />
            </Box>

            <Box gridColumn={ALL_COLS}>
              <Heading size={RADIX.HEADING.SIZE.SM}>
                Override Breaking Seams
              </Heading>
            </Box>

            <Box gridColumn={LEFT_COLS}>
              <Text color={RADIX.COLOR.SECONDARY}>
                Use (0,0) for all seam orientations for breaking pitches.
              </Text>
            </Box>
            <Box>
              <CommonSwitchInput
                id="pitch-uploader-zero-breaking-seams"
                checked={options.zeroBreakingSeams}
                onCheckedChange={(v) => {
                  useCookiesStore.getState().setCookie(CookieKey.app, {
                    pitch_upload_options: {
                      ...options,
                      zeroBreakingSeams: v,
                    },
                  });
                }}
              />
            </Box>
          </>
        )}

        <Box gridColumn={ALL_COLS}>
          <Separator size="4" />
        </Box>

        <Box gridColumn={ALL_COLS}>
          <Heading size={RADIX.HEADING.SIZE.SM}>{t('pu.select-file')}</Heading>
        </Box>

        <Box gridColumn={LEFT_COLS}>
          <CommonLink url={t('common.intercom-url') + HELP_URLS.PITCH_UPLOADER}>
            {t('pu.download-the-file-template')}
          </CommonLink>
        </Box>
        <Box>
          <CommonFileUploader
            id="pitch-uploader-upload-progress"
            progress={0}
            acceptedTypes={[TextMime.CSV]}
            notifyMode="each"
            onChange={async (files) => {
              if (files.length === 0) {
                return;
              }

              const formData = new FormData();
              formData.append('csv', files[0]);

              const fileName = files[0].name;

              const jsonData: ICSVRow[] =
                await MainService.getInstance().convertCSVToJSON(formData);

              if (jsonData.length > MAX_LINES) {
                NotifyHelper.error({
                  message_md: t('pu.upload-max-x-pitches-msg', {
                    x: MAX_LINES,
                  }),
                });
                return;
              }

              const format = CSVBall.detectFormat(first(jsonData));

              const balls = jsonData.map(
                (data, index) =>
                  new CSVBall({
                    releaseFt: machine.releaseFt,
                    format: format,
                    data: data,
                    index: index,
                    videosDict: lookupDict,
                    priority: options.priority,
                    zeroBreakingSeams: options.zeroBreakingSeams,
                  })
              );

              /** if any pitch upload options were selected, perform them after chars have been set for each ball */
              if (
                env.enable.pitch_upload_avg_release &&
                options.average_release
              ) {
                CSVBall.updateAverageReleasesByPitcher(balls);
              }

              if (env.enable.pitch_upload_avg_chars && options.average_pitch) {
                const avgPitches =
                  await CSVBall.getAveragesByPitcherAndType(balls);

                switch (options.average_pitch) {
                  case 'replace': {
                    /** delete existing entries before replacing */
                    balls.splice(0, balls.length, ...avgPitches);
                    break;
                  }

                  case 'append': {
                    /** append averages to the end of the list */
                    balls.push(...avgPitches);
                    break;
                  }

                  default: {
                    break;
                  }
                }
              }

              const validBalls = balls.filter((b) => !b.fatalError);
              const invalidBalls = balls.filter((b) => !!b.fatalError);

              /** notify if any CSV rows failed to parse w/ fatal error */
              if (invalidBalls.length > 0) {
                NotifyHelper.warning({
                  message_md: t('pu.excluded-x-invalid-rows-msg', {
                    x: invalidBalls.length,
                  }),
                });
              }

              usePitchUploaderStore.setState({
                filename: fileName,
                format: format,
                balls: balls,
                tab:
                  invalidBalls.length > 0
                    ? PitchUploaderTab.Errors
                    : PitchUploaderTab.BallData,
                activeID: first(validBalls)?._id ?? null,
                step: PitchUploadStep.preview,
              });

              usePitchUploaderStore.getState().aggregateWarnings(balls);

              /** block navigation with confirmation while preview data exists */
              usePitchUploaderStore.getState().setDirty(true);
            }}
          />
        </Box>
      </Grid>
    </ErrorBoundary>
  );
};
